import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useActionData, useFetcher } from 'react-router-dom';
import AddressBox from '../../components/data/address-box.jsx';
import { AddressSelector } from '../../components/project';
import { Modal } from '../../components/ui';
import { useMultipleAddresses } from '../../hooks';
import styles from './project-item-address.module.scss';

const hasText = (value) => typeof value === 'string' ? value.trim().length > 0 : !!value

export const MultipleDeliveryAddresses = ({ customerAddresses, isConfirmed, itemAddresses, totalQuantity, addressesCount, onChange }) => {
    const { t } = useTranslation()
    const [addresses, updateAddress, isValid, remainingQuantity] = useMultipleAddresses({ initialAddresses: itemAddresses, totalQuantity, addressesCount, onChange })
    const { errors = {} } = useActionData() || {}
    const [addressPreview, setAddressPreview] = useState(false)
    const fetcher = useFetcher()

    const handleConfirm = (addresses) => {
        try {
            fetcher.submit({
                addresses,
                action: 'multipleDeliveryAddress'
            }, {
                method: 'post',
                encType: 'application/json'
            })
        }
        catch (e) {
            return { error: e }
        }
    }
    if (isConfirmed) {
        return (
            <div>
                <p>{t("Below are the addresses confirmed by you")}</p>
                <div>
                    {addresses.map((address) => (
                        <div key={address.id} className={styles.addressSummary} style={{ marginBottom: 15 }} >
                            <div>
                                <AddressBox address={customerAddresses.find(add => add.id === address.address_id)} quantity={address.quantity} />
                            </div>
                            <div style={{ textAlign: "left" }}>
                                <p><strong>{t("Quantity")}</strong> : {address.quantity}</p>
                                {hasText(address.notes) && <p><strong>{t("Notes")} </strong>: {address.notes}</p>}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        )
    }
    return (
        <div className={`${styles.deliveryList} ${styles.multiDelivery}`}>
            {addresses.map(({ id, address_id, quantity, notes }, index) => (
                <div key={id} className={`box--light-gray ${styles.addressBox}`}>
                    <div className={styles.deliveryHeading}>
                        <span className={styles.deliveryNumber}>{index + 1}</span>
                        {t('Delivery')} {index + 1} / {addresses.length}
                    </div>
                    <label>
                        {t('Quantity')}
                        <input
                            type="number"
                            min={0}
                            step={1}
                            max={totalQuantity}
                            name={`addresses[${index}][quantity]`}
                            value={quantity}
                            onChange={(e) => updateAddress(id, { address_id, notes, quantity: e.target.valueAsNumber })}
                            onWheel={(e) => updateAddress(id, { address_id, notes, quantity: e.target.valueAsNumber })}
                            onFocus={e => e.target.select()}
                            required
                        />
                        {!!errors[`addresses[${index}][quantity]`] && errors[`addresses[${index}][quantity]`].map((error, index) => <p key={index} className='error'>{error}</p>)}
                    </label>
                    {!!errors[`addresses[${index}][address_id]`] && errors[`addresses[${index}][address_id]`].map((error, index) => <p key={index} className='error'>{error}</p>)}
                    <AddressSelector
                        name={`addresses[${index}][address_id]`}
                        addresses={customerAddresses}
                        selectedAddressId={address_id ?? null}
                        onChange={(address_id) => updateAddress(id, { address_id, notes, quantity })}
                        excludedAddresses={addresses.map(({ address_id }) => address_id).filter(v => v !== address_id)}
                    />
                    <label>{t("Notes")}</label>
                    <textarea name={`addresses[${index}][notes]`} rows={4} style={{ width: "100%" }} defaultValue={notes} onChange={(e) => updateAddress(id, { address_id, quantity, notes: e.target.value })} />
                </div>
            ))}
            <Modal title={t("Selected Address")} isVisible={addressPreview} onClose={() => {
                setAddressPreview(false)
            }}>
                <div className={fetcher.state !== 'idle' ? 'loading' : ''}>
                    <p>{t("Please confirm your selection of these addresses as it will be final and cannot be changed later.")}</p>
                    <div  >
                        {addresses.filter(({ address_id }) => !!address_id).map((address) => {
                            return <div key={address.id} className={styles.addressSummary} style={{ marginBottom: 15 }}>
                                <div>
                                    <AddressBox address={customerAddresses.find(add => add.id === address.address_id)} quantity={address.quantity} />
                                </div>
                                <div style={{ textAlign: "left" }}>
                                    <p><strong>{t("Quantity")}</strong> : {address.quantity}</p>
                                    {hasText(address.notes) && <p><strong>{t("Notes")} </strong>: {address.notes}</p>}
                                </div>
                            </div>
                        })}

                    </div>
                    <br />
                    <div className="actions">
                        <button className="button--primary button--large" onClick={() => { handleConfirm(addresses) }} disabled={fetcher.state !== 'idle'}>{t("Yes")}</button>
                        <button className="button--cancel button--large" onClick={() => setAddressPreview(false)} disabled={fetcher.state !== 'idle'}>{t("Cancel")}</button>
                    </div>
                </div>
            </Modal >
            <div className={`actions align--right ${styles.saveBar}`}>
                {remainingQuantity > 0 && (
                    <div className={`error-message ${styles.saveStatus} ${styles.saveStatusRemaining}`} style={{ padding: '0 var(--space-small)' }}>{t('Remaining Quantity')}: <span className={styles.saveStatusValue}>{remainingQuantity}</span></div>
                )}
                {remainingQuantity < 0 && (
                    <div className={`error-message ${styles.saveStatus}`} style={{ padding: '0 var(--space-small)' }}>{t('Quantity exceeds ordered quantity')}: <span className={styles.saveStatusValue}>{-remainingQuantity}</span></div>
                )}
                <button className="button--primary" onClick={() => { setAddressPreview(true) }} disabled={!isValid}>{t('Save')}</button>
            </div>
        </div >
    )
}
MultipleDeliveryAddresses.propTypes = {
    customerAddresses: PropTypes.arrayOf(PropTypes.object).isRequired,
    onChange: PropTypes.func.isRequired,
    itemAddresses: PropTypes.arrayOf(PropTypes.object).isRequired,
    addressesCount: PropTypes.number.isRequired,
    totalQuantity: PropTypes.number.isRequired,
    isConfirmed: PropTypes.bool,

}

export default MultipleDeliveryAddresses
